import { useState, useMemo, useCallback } from "react";
import { useGetAuthOptionsQuery, useGoogleOAuthLoginMutation } from "@/services/authApi";

type AuthFlowStatus = "idle" | "loading" | "error";

interface UseAuthFlowResult {
    status: AuthFlowStatus;
    errorMessage: string | null;
    handleGoogleCredential: (credential: string) => Promise<void>;
}

export const useAuthFlow = (): UseAuthFlowResult => {
    const { data: authOptions } = useGetAuthOptionsQuery();
    const [googleOAuthLogin, { isLoading: googleLoading }] =
        useGoogleOAuthLoginMutation();

    const [status, setStatus] = useState<AuthFlowStatus>("idle");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const googleClientId = authOptions?.google_oauth2?.[0]?.google_client_id;

    const handleGoogleCredential = useCallback(
        async (credential: string) => {
            setStatus("loading");
            setErrorMessage(null);

            if (!googleClientId) {
                setStatus("error");
                setErrorMessage(
                    "Authentication configuration not available. Please try again later."
                );
                return;
            }

            try {
                await googleOAuthLogin({
                    credential,
                    clientId: googleClientId,
                }).unwrap();

                setStatus("idle");
                setErrorMessage(null);
            } catch (e: any) {
                setStatus("error");
                setErrorMessage(
                    e?.data?.message ||
                    e?.message ||
                    "Google authentication failed. Please try again."
                );
            }
        },
        [googleClientId, googleOAuthLogin]
    );

    const derivedStatus: AuthFlowStatus = useMemo(() => {
        if (status === "loading" || googleLoading) return "loading";
        return status;
    }, [status, googleLoading]);

    return {
        status: derivedStatus,
        errorMessage,
        handleGoogleCredential,
    };
};